import {useEffect, useState} from 'react';
import {Layout} from '@/components/Layout';
import {Card} from '@/components/ui/card';
import {Button} from '@/components/ui/button';
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select';
import {Label} from '@/components/ui/label';
import {Badge} from '@/components/ui/badge';
import {toast} from '@/hooks/use-toast';
import {useLookupsSnapshot, toOptionLabel} from '@/features/lookups/snapshot';
import {adoptionWaitApi, AdoptionWaitRequest} from './api';
import {Bell, Loader2, Search} from 'lucide-react';

const NONE = '__none__';

function label(item?: any) {
  return item ? toOptionLabel(item) : 'Cualquiera';
}

function formatAgeMonths(months?: number | null) {
  if (months == null) return '';
  const normalized = Math.max(0, Number(months));
  const years = Math.floor(normalized / 12);
  const remainingMonths = normalized % 12;
  if (years && remainingMonths) return `${years} ${years === 1 ? 'año' : 'años'} y ${remainingMonths} ${remainingMonths === 1 ? 'mes' : 'meses'}`;
  if (years) return `${years} ${years === 1 ? 'año' : 'años'}`;
  return `${remainingMonths} ${remainingMonths === 1 ? 'mes' : 'meses'}`;
}

function ageLabel(row: AdoptionWaitRequest) {
  if (row.minAgeMonths == null && row.maxAgeMonths == null) return 'Cualquier edad';
  if (row.minAgeMonths != null && row.maxAgeMonths != null) return `${formatAgeMonths(row.minAgeMonths)} a ${formatAgeMonths(row.maxAgeMonths)}`;
  if (row.minAgeMonths != null) return `Desde ${formatAgeMonths(row.minAgeMonths)}`;
  return `Hasta ${formatAgeMonths(row.maxAgeMonths)}`;
}

function formatDate(value?: string | null) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

export default function AdoptionWaitRequestsPage() {
  const {data: lookups} = useLookupsSnapshot();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<AdoptionWaitRequest[]>([]);
  const [filters, setFilters] = useState<{speciesId?: number; sexId?: number; sizeId?: number}>({});

  const load = async () => {
    setLoading(true);
    try {
      const clean = Object.fromEntries(Object.entries(filters).filter(([, v]) => v != null));
      setRows(await adoptionWaitApi.listActive(clean));
    } catch (error: any) {
      toast({variant: 'destructive', title: 'Error', description: error?.response?.data?.error?.message || 'No se pudieron cargar las solicitudes.'});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center gap-3">
          <Bell className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-3xl font-bold">Solicitudes de espera</h1>
            <p className="text-muted-foreground">Consulta qué perfiles de animales están buscando los adoptantes.</p>
          </div>
        </div>

        <Card className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <FilterSelect label="Especie" value={filters.speciesId} items={lookups?.species || []} onChange={(v) => setFilters({...filters, speciesId: v})} />
          <FilterSelect label="Sexo" value={filters.sexId} items={lookups?.sexes || []} onChange={(v) => setFilters({...filters, sexId: v})} />
          <FilterSelect label="Tamaño" value={filters.sizeId} items={lookups?.sizes || []} onChange={(v) => setFilters({...filters, sizeId: v})} />
          <Button onClick={load}><Search className="h-4 w-4 mr-2" />Buscar</Button>
        </Card>

        {loading ? (
          <Card className="p-6 flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Cargando...</Card>
        ) : rows.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground">No hay solicitudes activas con estos filtros.</Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {rows.map((row) => (
              <Card key={row.id} className="p-5 space-y-4">
                <div className="flex justify-between gap-3">
                  <div>
                    <h2 className="font-semibold">Preferencia de adopción</h2>
                    <p className="text-sm text-muted-foreground">{formatDate(row.createdAt)}</p>
                  </div>
                  <Badge>Activa</Badge>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <Summary label="Adoptante" value={row.adopterName || 'No informado'} />
                  <Summary label="Celular" value={row.adopterPhone || 'No informado'} />
                  <Summary label="Especie" value={label(row.species)} />
                  <Summary label="Sexo" value={label(row.sex)} />
                  <Summary label="Tamaño" value={label(row.size)} />
                  <Summary label="Edad" value={ageLabel(row)} />
                  <Summary label="Ciudad" value={row.adopterCity || 'No informada'} />
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

function FilterSelect({label, value, items, onChange}: {label: string; value?: number; items: any[]; onChange: (value?: number) => void}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Select value={value ? String(value) : NONE} onValueChange={(v) => onChange(v === NONE ? undefined : Number(v))}>
        <SelectTrigger><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>Todos</SelectItem>
          {items.map(item => <SelectItem key={item.id} value={String(item.id)}>{toOptionLabel(item)}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function Summary({label, value}: {label: string; value: string}) {
  return <div><p className="text-muted-foreground">{label}</p><p className="font-medium">{value}</p></div>;
}
